import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from './config';
import { getAccessToken, getCachedUserId } from './utils/auth';
import useProgressPolling from './utils/useProgressPolling';
import { deriveBlitzDone, deriveBlitzInFlight } from './utils/blitzPhase';
import * as repository from './utils/offline/repository';
import BlitzPlayer from './components/blitz/BlitzPlayer';
import BlitzNotReadyView from './components/blitz/BlitzNotReadyView';

/**
 * Blitz — route shell at `/blitz/:projectId` (spec 011).
 *
 * Fetches the project payload and chapters in parallel, then renders either:
 *   - <BlitzNotReadyView> when Phase 3 has never been kicked off.
 *   - <BlitzPlayer> when Phase 3 is in flight OR complete.
 *
 * Phase-3 "done" detection uses `progressSnapshot.blitz_state` from
 * `GET /projects/{id}/progress` (v0.12.0).
 */
function Blitz() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async ({ signal } = {}) => {
    const token = getAccessToken();
    const headers = { Authorization: `Bearer ${token}` };
    const [projectRes, chaptersRes] = await Promise.all([
      axios.get(getApiUrl(`projects/${projectId}`), { headers, signal }),
      axios.get(getApiUrl(`projects/${projectId}/chapters`), { headers, signal }),
    ]);
    return {
      project: projectRes.data || {},
      chapters: chaptersRes.data.chapters || [],
    };
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    fetchData({ signal: controller.signal })
      .then((data) => {
        if (!cancelled) {
          setProject(data.project);
          setChapters(data.chapters);
          setLoading(false);
        }
      })
      .catch(async (e) => {
        if (cancelled || e?.name === 'CanceledError' || e?.name === 'AbortError') return;
        // Offline: fall back to whatever the device holds so the reading view
        // still opens (FR-002). Chapters without cached text render their
        // existing "not yet available" state rather than an error.
        try {
          const [cachedProject, cachedChapters] = await Promise.all([
            repository.getProject(projectId),
            repository.getChapters(projectId),
          ]);
          if (!cancelled && cachedProject) {
            setProject(cachedProject);
            setChapters(cachedChapters);
            setLoading(false);
            return;
          }
        } catch (storeErr) {
          // no account bound, or the store is unavailable
        }
        if (cancelled) return;
        setError('Failed to load project. Please try again.');
        setLoading(false);
      });
    return () => { cancelled = true; controller.abort(); };
  }, [fetchData, projectId]);

  const isOwner = !!project?.owner_id && project.owner_id === getCachedUserId();
  const fileTitle = project?.title || 'Untitled';
  const autoPhase3 = project?.auto_blitz === true;

  // Poll until blitz_state is done
  const pollingEnabled = !!(project && !loading);
  const handleProgressDone = useCallback(() => {
    fetchData()
      .then((data) => {
        setProject(data.project);
        setChapters(data.chapters);
      })
      .catch(() => {});
  }, [fetchData]);
  const handleProgressAuthError = useCallback((err) => {
    if (err?.response?.status === 401) {
      setError('Unauthorized: Please login again.');
    }
  }, []);

  const { snapshot: progressSnapshot } = useProgressPolling(
    projectId,
    pollingEnabled,
    {
      onDone: handleProgressDone,
      onError: handleProgressAuthError,
      isDone: (snap) => snap?.blitz_state === 'done',
    },
  );

  const blitzState = progressSnapshot?.blitz_state || null;
  const audioState = progressSnapshot?.audio_state || null;

  // Derive Phase 3 state from the project detail (from GET /projects/{id}) as
  // well as the live polling snapshot, so the in-flight / done view shows
  // immediately on reload instead of falling back to the "Generate Blitz" CTA.
  const projectFiles = Array.isArray(project?.files) ? project.files : [];
  const snapshotFiles = Array.isArray(progressSnapshot?.files) ? progressSnapshot.files : [];
  const phase3IsDone = deriveBlitzDone(blitzState, projectFiles);
  const phase3IsInFlight = deriveBlitzInFlight({
    phase3IsDone, blitzState, audioState, autoPhase3, projectFiles, snapshotFiles,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <Link to={`/projects/${projectId}/chapters`} className="text-purple-400 hover:underline">
            ← Back to chapters
          </Link>
        </div>
      </div>
    );
  }

  const handleExit = () => navigate(`/projects/${projectId}/chapters`);

  if (!phase3IsDone && !phase3IsInFlight) {
    return (
      <BlitzNotReadyView
        fileId={projectId}
        fileTitle={fileTitle}
        chapters={chapters}
        phase3Status={blitzState || 'not_started'}
        phase2Status={audioState || 'not_started'}
        autoPhase3={autoPhase3}
        isOwner={isOwner}
        onExit={handleExit}
        blitzStageState={blitzState}
        progressSnapshot={progressSnapshot}
      />
    );
  }

  return (
    <BlitzPlayer
      fileId={projectId}
      fileTitle={fileTitle}
      chapters={chapters}
      progressSnapshot={progressSnapshot}
      isPhase3InFlight={phase3IsInFlight}
    />
  );
}

export default Blitz;
